// FLEXX Trading Tools Hub — 6 client-side trader calculators + checklist.
// Route: /tools (gated by auth, no specific course access required).
// All math runs in the browser. No persistence — tools reset on close.
//
// Tools:
//   1. XAUUSD Lot Calculator         — Gold MT5 sizing
//   2. Risk / Reward Calculator       — generic R:R ratio + breakeven WR
//   3. Pip Value Calculator           — per-lot pip value across instruments
//   4. PMIR Signal Calculator         — copy-paste signal → instant lot size
//   5. BTCUSD Lot Calculator          — Bitcoin sizing with high-exposure warn
//   6. Pre-Trade Checklist            — 8-item disciplined entry gate
//
// Cross-tool integration:
//   "LOG THIS TRADE" / "LOG PMIR TRADE" buttons stash a pre-fill payload to
//   sessionStorage["flexx_journal_prefill"] then route to /#journal — the
//   journal's defaultForm() picks it up on mount and clears the key.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { THEME } from "../state";
import Portal from "../Portal";
import MarketIntel from "./MarketIntel";

const T = {
  bg: "#060606",
  card: "#0d1218",
  border: "#1C1C1C",
  gold: "#C9A227",
  goldDim: "#8a7220",
  text: "#EDE8DC",
  mute: "#AAAAAA",
  green: "#22C55E",
  yellow: "#FFB347",
  red: "#FF4444",
};

const CARDS = [
  { id: "xauusd",    icon: "🥇", title: "XAUUSD Lot Calculator",   desc: "Gold lot sizing for MT5 — 0.01 lot precision." },
  { id: "rr",        icon: "⚡", title: "Risk / Reward Calculator", desc: "Compute R:R and breakeven win rate live." },
  { id: "pip",       icon: "📐", title: "Pip Value Calculator",     desc: "Pip value across XAU / BTC / FX majors." },
  { id: "pmir",      icon: "🎯", title: "PMIR Signal Calculator",   desc: "Paste a signal — get your lot size instantly." },
  { id: "btc",       icon: "📊", title: "BTC Lot Calculator",       desc: "Bitcoin lot sizing with exposure warnings." },
  { id: "checklist", icon: "🔲", title: "Pre-Trade Checklist",      desc: "8 mandatory checks before every entry." },
];

export default function ToolsHub() {
  const [openTool, setOpenTool] = useState(null);

  return (
    <div data-testid="tools-hub" style={{
      minHeight: "100vh", background: T.bg, color: T.text,
      paddingBottom: 96, // room for mobile bottom-tab bar
    }}>
      <div style={{ maxWidth: 880, margin: "0 auto", padding: "32px 16px 16px" }}>
        <div style={{ fontFamily: "monospace", color: T.gold, fontSize: 11, letterSpacing: 2.5 }}>FLEXX ACADEMY · TRADING UTILITIES</div>
        <h1 style={{
          margin: "8px 0 4px", fontSize: 38, fontFamily: "'Bebas Neue','Oswald',sans-serif",
          letterSpacing: 0.5, color: T.text,
        }} data-testid="tools-hub-title">FLEXX TOOLS</h1>
        <div style={{ color: T.mute, fontSize: 14, fontStyle: "italic" }} data-testid="tools-hub-subheader">
          Open before every trade.
        </div>

        <div style={{
          marginTop: 24,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 14,
        }}>
          {CARDS.map((c) => (
            <button key={c.id} data-testid={`tool-card-${c.id}`}
              onClick={() => setOpenTool(c.id)}
              style={{
                all: "unset", cursor: "pointer", display: "block",
                background: T.card,
                border: `1px solid ${T.border}`,
                borderLeft: `3px solid ${T.gold}`,
                borderRadius: 12, padding: 18,
                transition: "transform 0.18s, border-color 0.18s, box-shadow 0.18s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.gold; e.currentTarget.style.boxShadow = `0 0 24px ${T.gold}22`; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.boxShadow = "none"; }}>
              <div style={{ fontSize: 32, lineHeight: 1, marginBottom: 8 }}>{c.icon}</div>
              <div style={{ color: T.text, fontWeight: 800, fontSize: 16, marginBottom: 4 }}>{c.title}</div>
              <div style={{ color: T.mute, fontSize: 12, lineHeight: 1.5, marginBottom: 12 }}>{c.desc}</div>
              <span data-testid={`tool-open-${c.id}`} style={{
                display: "inline-block", padding: "6px 14px", borderRadius: 6,
                background: `linear-gradient(135deg, ${T.gold}, #E8B94F)`,
                color: "#0a0a0a", fontWeight: 800, fontFamily: "monospace",
                fontSize: 11, letterSpacing: 1.5,
              }}>OPEN →</span>
            </button>
          ))}
        </div>

        {/* Market Intel — live Fear & Greed + Gold pivots + DXY bias */}
        <MarketIntel />
      </div>

      {openTool && <ToolSheet toolId={openTool} onClose={() => setOpenTool(null)} />}
    </div>
  );
}

// ── Sheet wrapper — slide-up from bottom ─────────────────────────────────
function ToolSheet({ toolId, onClose }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 10);
    document.body.style.overflow = "hidden";
    return () => { clearTimeout(t); document.body.style.overflow = ""; };
  }, []);
  const titleMap = {
    xauusd: "Gold Lot Size Calculator", rr: "Risk : Reward Calculator",
    pip: "Pip Value Calculator", pmir: "PMIR Signal Calculator",
    btc: "BTC Lot Size Calculator", checklist: "Pre-Trade Checklist",
  };
  return (
    <Portal>
      <div data-testid="tool-sheet" role="dialog" aria-label={titleMap[toolId]}
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, zIndex: 12000,
          background: "rgba(0,0,0,0.78)", backdropFilter: "blur(8px)",
          display: "flex", alignItems: "flex-end", justifyContent: "center",
        }}>
        <div onClick={(e) => e.stopPropagation()}
          data-testid={`tool-sheet-${toolId}`}
          style={{
            background: T.card, borderTop: `2px solid ${T.gold}`,
            borderRadius: "16px 16px 0 0",
            width: "min(720px, 100%)", maxHeight: "92vh", overflowY: "auto",
            transform: mounted ? "translateY(0)" : "translateY(100%)",
            transition: "transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)",
            boxShadow: `0 -10px 60px ${T.gold}33`,
          }}>
          <div style={{ position: "sticky", top: 0, zIndex: 1, display: "flex",
            justifyContent: "space-between", alignItems: "center",
            padding: "14px 18px", background: T.card, borderBottom: `1px solid ${T.border}` }}>
            <div style={{ width: 40, height: 4, borderRadius: 2, background: T.border, position: "absolute", left: "50%", top: 6, transform: "translateX(-50%)" }} />
            <div>
              <div style={{ fontFamily: "monospace", color: T.gold, fontSize: 9, letterSpacing: 2 }}>FLEXX TOOLS</div>
              <div style={{ color: T.text, fontWeight: 800, fontSize: 17, marginTop: 2 }}>{titleMap[toolId]}</div>
            </div>
            <button data-testid="tool-sheet-close" onClick={onClose} style={{
              all: "unset", cursor: "pointer",
              padding: "6px 12px", borderRadius: 6,
              border: `1px solid ${T.border}`, color: T.mute,
              fontFamily: "monospace", fontSize: 11, letterSpacing: 1,
            }}>CLOSE ✕</button>
          </div>

          <div style={{ padding: 18 }}>
            {toolId === "xauusd"    && <XauusdLotCalc onClose={onClose} />}
            {toolId === "rr"        && <RiskRewardCalc />}
            {toolId === "pip"       && <PipValueCalc />}
            {toolId === "pmir"      && <PmirSignalCalc onClose={onClose} />}
            {toolId === "btc"       && <BtcLotCalc />}
            {toolId === "checklist" && <PreTradeChecklist />}
          </div>
        </div>
      </div>
    </Portal>
  );
}

// ── Shared form atoms ────────────────────────────────────────────────────
const fieldStyle = {
  display: "block", marginBottom: 14,
};
const labelStyle = {
  color: T.mute, fontSize: 11, letterSpacing: 1, textTransform: "uppercase",
  fontFamily: "monospace", marginBottom: 6, display: "block",
};
const inputStyle = {
  width: "100%", padding: "11px 13px", borderRadius: 8,
  background: "#13161c", border: `1px solid ${T.border}`,
  color: T.text, fontSize: 14, outline: "none", boxSizing: "border-box",
  fontFamily: "monospace",
};
const goldBigNum = {
  fontSize: 36, fontWeight: 900, color: T.gold,
  fontFamily: "'Bebas Neue','Oswald',monospace", letterSpacing: 1,
};
const statBox = {
  background: "#0a0e13", border: `1px solid ${T.border}`,
  borderRadius: 10, padding: 12,
};
const goldBtn = {
  all: "unset", cursor: "pointer", textAlign: "center",
  display: "block", width: "100%", boxSizing: "border-box",
  padding: "13px 18px", borderRadius: 8,
  background: `linear-gradient(135deg, ${T.gold}, #E8B94F)`,
  color: "#0a0a0a", fontFamily: "monospace", fontSize: 13, letterSpacing: 1.5,
  fontWeight: 800, textTransform: "uppercase", marginTop: 16,
};

function Field({ label, children, testid }) {
  return (
    <label data-testid={testid} style={fieldStyle}>
      <span style={labelStyle}>{label}</span>
      {children}
    </label>
  );
}

function ResultRow({ label, value, color, testid, big }) {
  return (
    <div data-testid={testid} style={statBox}>
      <div style={{ color: T.mute, fontFamily: "monospace", fontSize: 10, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6 }}>{label}</div>
      <div style={{ ...(big ? goldBigNum : { fontSize: 18, fontWeight: 800 }), color: color || T.gold }}>{value}</div>
    </div>
  );
}

// ── 1. XAUUSD LOT CALCULATOR ─────────────────────────────────────────────
function XauusdLotCalc({ onClose }) {
  const navigate = useNavigate();
  const [balance, setBalance] = useState("1000");
  const [risk, setRisk] = useState("1");
  const [slValue, setSlValue] = useState("20");
  const [slUnit, setSlUnit] = useState("PIPS"); // "POINTS" | "PIPS"
  const [ccy, setCcy] = useState("USD");

  // XAUUSD MT5 spec:
  //   POINTS (MT5/Errante default): 1 point = $0.01 price move ⇒ pip value per std lot = $1.00
  //     Formula: Lot = Dollar Risk / (SL points × $1.00)
  //   PIPS (retail/educator convention): 1 pip = $0.10 price move = 10 points
  //     ⇒ pip value per std lot = $10.00
  //     Formula: Lot = Dollar Risk / (SL pips × $10.00)
  const dollarPerUnitPerLot = slUnit === "POINTS" ? 1.0 : 10.0;
  const calc = useMemo(() => {
    const bal = Number(balance) || 0;
    const r = Number(risk) || 0;
    const sl = Number(slValue) || 0;
    const dollarRisk = bal * (r / 100);
    if (sl <= 0) return { dollarRisk, lot: 0, pipValuePerLot: 0 };
    const lotRaw = dollarRisk / (sl * dollarPerUnitPerLot);
    const lot = Math.max(0, Math.floor(lotRaw * 100) / 100); // round down 0.01
    return { dollarRisk, lot, pipValuePerLot: lot * dollarPerUnitPerLot };
  }, [balance, risk, slValue, dollarPerUnitPerLot]);

  const tpProfit = calc.dollarRisk * 2; // auto 2R
  const tpProfitPct = (Number(risk) || 0) * 2;

  const ccyLabel = (v) => {
    if (ccy === "USD") return `$${v.toFixed(2)}`;
    if (ccy === "PHP") return `≈ ₱${(v * 56).toFixed(0)} (USD ${v.toFixed(2)})`;
    if (ccy === "AED") return `≈ AED ${(v * 3.67).toFixed(2)} (USD ${v.toFixed(2)})`;
    return `$${v.toFixed(2)}`;
  };

  const logTrade = () => {
    const payload = {
      market: "XAUUSD",
      riskPct: String(risk),
      lotSize: calc.lot,
      slValue: Number(slValue),
      slUnit,
      dollarRisk: calc.dollarRisk,
      source: `XAUUSD Lot Calculator (${slUnit})`,
    };
    try { sessionStorage.setItem("flexx_journal_prefill", JSON.stringify(payload)); } catch (e) { console.warn("[FLEXX Tools] sessionStorage prefill failed:", e?.message || e); }
    onClose?.();
    navigate("/");
    setTimeout(() => { window.location.hash = "journal"; }, 30);
  };

  return (
    <div data-testid="xauusd-lot-calc">
      <div style={{ display: "inline-block", padding: "4px 10px", borderRadius: 999, border: `1px solid ${T.gold}`, color: T.gold, fontFamily: "monospace", fontSize: 10, letterSpacing: 1.5, marginBottom: 14 }}>
        XAUUSD · MT5
      </div>
      <Field label="Account Balance ($)" testid="xau-field-balance">
        <input data-testid="xau-balance-input" type="number" inputMode="decimal" min="0" step="any"
          value={balance} onChange={(e) => setBalance(e.target.value)} style={inputStyle} />
      </Field>
      <Field label={`Risk %  —  ${risk}%`} testid="xau-field-risk">
        <input data-testid="xau-risk-slider" type="range" min="0.5" max="5" step="0.1"
          value={risk} onChange={(e) => setRisk(e.target.value)}
          style={{ width: "100%", accentColor: T.gold }} />
      </Field>

      <Field label={`Stop Loss (${slUnit === "POINTS" ? "points" : "pips"})`} testid="xau-field-sl">
        <div data-testid="xau-sl-unit-toggle" style={{ display: "flex", gap: 6, marginBottom: 8 }}>
          {["POINTS", "PIPS"].map((u) => {
            const active = slUnit === u;
            return (
              <button key={u} type="button" data-testid={`xau-sl-unit-${u.toLowerCase()}`}
                onClick={() => setSlUnit(u)}
                style={{
                  all: "unset", cursor: "pointer", flex: 1, textAlign: "center",
                  padding: "8px 12px", borderRadius: 6,
                  background: active ? T.gold : "transparent",
                  color: active ? "#0a0a0a" : T.mute,
                  border: `1px solid ${active ? T.gold : T.border}`,
                  fontFamily: "monospace", fontSize: 11, letterSpacing: 1.5, fontWeight: 800,
                }}>{u}</button>
            );
          })}
        </div>
        <input data-testid="xau-sl-input" type="number" inputMode="decimal" min="0" step="any"
          value={slValue} onChange={(e) => setSlValue(e.target.value)} style={inputStyle} />
        <div data-testid="xau-sl-hint" style={{ marginTop: 6, color: T.mute, fontSize: 11, fontStyle: "italic", lineHeight: 1.5 }}>
          {slUnit === "POINTS"
            ? "Use this if MT5 shows '668 points'"
            : "Use this if your mentor says '50 pips SL'"}
        </div>
      </Field>

      <Field label="Account Currency" testid="xau-field-ccy">
        <select data-testid="xau-ccy-select" value={ccy} onChange={(e) => setCcy(e.target.value)} style={inputStyle}>
          <option>USD</option><option>PHP</option><option>AED</option>
        </select>
      </Field>

      <div data-testid="xau-sl-info-note" style={{
        marginBottom: 14, padding: 10, borderRadius: 8,
        background: `${T.yellow}11`, border: `1px solid ${T.yellow}55`,
        color: T.text, fontSize: 12, lineHeight: 1.5,
      }}>
        ⚠️ MT5 shows SL in <b style={{ color: T.yellow }}>POINTS</b> (e.g. 668 pts ≈ ~67 pips).
        Toggle above to match how you measure your stop loss.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginTop: 18 }}>
        <ResultRow testid="xau-result-lot" label="Recommended Lot Size" value={calc.lot.toFixed(2)} big />
        <ResultRow testid="xau-result-risk" label="Dollar Risk" value={ccyLabel(calc.dollarRisk)} color={T.text} />
        <ResultRow testid="xau-result-pipvalue"
          label={slUnit === "POINTS" ? "$ / point @ this lot" : "$ / pip @ this lot"}
          value={`$${calc.pipValuePerLot.toFixed(2)}/${slUnit === "POINTS" ? "point" : "pip"}`} color={T.text} />
      </div>

      <div data-testid="xau-result-sl-text" style={{ marginTop: 12, padding: 12, background: `${T.red}11`, border: `1px solid ${T.red}55`, borderRadius: 8, color: T.text, fontSize: 13 }}>
        If SL hit: you lose <b style={{ color: T.red }}>{ccyLabel(calc.dollarRisk)}</b> ({Number(risk).toFixed(1)}% of account)
      </div>
      <div data-testid="xau-result-tp-text" style={{ marginTop: 8, padding: 12, background: `${T.green}11`, border: `1px solid ${T.green}55`, borderRadius: 8, color: T.text, fontSize: 13 }}>
        If TP hit (auto 2R): you gain <b style={{ color: T.green }}>{ccyLabel(tpProfit)}</b> ({tpProfitPct.toFixed(1)}% of account)
      </div>

      <button data-testid="xau-log-trade-btn" onClick={logTrade} style={goldBtn}>
        LOG THIS TRADE →
      </button>
    </div>
  );
}

// ── 2. RISK / REWARD CALCULATOR ──────────────────────────────────────────
const RR_PIP_FACTORS = { XAUUSD: { pipPerUnit: 10, dollarPerPip: 1, pointPerUnit: 100 }, BTCUSD: { pipPerUnit: 1, dollarPerPip: 1, pointPerUnit: 100 }, EURUSD: { pipPerUnit: 10000, dollarPerPip: 10, pointPerUnit: 100000 }, GBPUSD: { pipPerUnit: 10000, dollarPerPip: 10, pointPerUnit: 100000 }, Other: { pipPerUnit: 10000, dollarPerPip: 10, pointPerUnit: 100000 } };

function RiskRewardCalc() {
  const [entry, setEntry] = useState("");
  const [sl, setSl] = useState("");
  const [tp, setTp] = useState("");
  const [lot, setLot] = useState("");
  const [instr, setInstr] = useState("XAUUSD");

  const calc = useMemo(() => {
    const e = parseFloat(entry), s = parseFloat(sl), t = parseFloat(tp);
    if (isNaN(e) || isNaN(s) || isNaN(t)) return null;
    const slDist = Math.abs(e - s);
    const tpDist = Math.abs(t - e);
    if (slDist === 0) return null;
    const rr = tpDist / slDist;
    const f = RR_PIP_FACTORS[instr] || RR_PIP_FACTORS.Other;
    const slPips = slDist * f.pipPerUnit;
    const tpPips = tpDist * f.pipPerUnit;
    const slPoints = slDist * f.pointPerUnit;
    const tpPoints = tpDist * f.pointPerUnit;
    const lotN = parseFloat(lot) || 0;
    const slDollar = lotN > 0 ? slPips * f.dollarPerPip * lotN : null;
    const tpDollar = lotN > 0 ? tpPips * f.dollarPerPip * lotN : null;
    const breakevenWR = rr > 0 ? (1 / (1 + rr)) * 100 : 0;
    let color = T.green, status = "GOOD";
    // Use epsilon to avoid float-precision misclassifying exact 1:2.00 ratios.
    if (rr < 1 - 1e-6) { color = T.red; status = "POOR"; }
    else if (rr < 2 - 1e-6) { color = T.yellow; status = "ACCEPTABLE"; }
    return { rr, slPips, tpPips, slPoints, tpPoints, slDollar, tpDollar, breakevenWR, color, status };
  }, [entry, sl, tp, lot, instr]);

  return (
    <div data-testid="rr-calc">
      <Field label="Instrument" testid="rr-field-instr">
        <select data-testid="rr-instr-select" value={instr} onChange={(e) => setInstr(e.target.value)} style={inputStyle}>
          <option>XAUUSD</option><option>BTCUSD</option><option>EURUSD</option><option>GBPUSD</option><option>Other</option>
        </select>
      </Field>
      <Field label="Entry Price" testid="rr-field-entry">
        <input data-testid="rr-entry-input" type="number" inputMode="decimal" step="any" value={entry} onChange={(e) => setEntry(e.target.value)} style={inputStyle} />
      </Field>
      <Field label="Stop Loss Price" testid="rr-field-sl">
        <input data-testid="rr-sl-input" type="number" inputMode="decimal" step="any" value={sl} onChange={(e) => setSl(e.target.value)} style={inputStyle} />
      </Field>
      <Field label="Take Profit Price" testid="rr-field-tp">
        <input data-testid="rr-tp-input" type="number" inputMode="decimal" step="any" value={tp} onChange={(e) => setTp(e.target.value)} style={inputStyle} />
      </Field>
      <Field label="Lot Size (optional)" testid="rr-field-lot">
        <input data-testid="rr-lot-input" type="number" inputMode="decimal" step="any" value={lot} onChange={(e) => setLot(e.target.value)} style={inputStyle} placeholder="e.g. 0.10" />
      </Field>

      {calc ? (
        <>
          <div style={{
            marginTop: 16, padding: 18, borderRadius: 12,
            background: `${calc.color}10`, border: `1px solid ${calc.color}`,
            textAlign: "center",
          }}>
            <div style={{ fontFamily: "monospace", color: calc.color, fontSize: 10, letterSpacing: 2 }}>R : R RATIO</div>
            <div data-testid="rr-result-ratio" style={{ ...goldBigNum, color: calc.color, fontSize: 48, marginTop: 4 }}>
              1 : {calc.rr.toFixed(2)}
            </div>
            <div data-testid="rr-result-status" style={{ color: calc.color, fontFamily: "monospace", fontSize: 11, letterSpacing: 1.5, marginTop: 6 }}>
              {calc.status}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginTop: 12 }}>
            <ResultRow testid="rr-result-sl-dist" label="SL Distance"
              value={`${calc.slPips.toFixed(1)} pips (${Math.round(calc.slPoints)} points)${calc.slDollar !== null ? ` · $${calc.slDollar.toFixed(2)}` : ""}`} color={T.text} />
            <ResultRow testid="rr-result-tp-dist" label="TP Distance"
              value={`${calc.tpPips.toFixed(1)} pips (${Math.round(calc.tpPoints)} points)${calc.tpDollar !== null ? ` · $${calc.tpDollar.toFixed(2)}` : ""}`} color={T.text} />
            <ResultRow testid="rr-result-breakeven" label="Breakeven Win Rate"
              value={`${calc.breakevenWR.toFixed(1)}%`} color={T.gold} />
          </div>
        </>
      ) : (
        <div style={{ marginTop: 14, padding: 14, color: T.mute, fontSize: 13, fontStyle: "italic", textAlign: "center", border: `1px dashed ${T.border}`, borderRadius: 10 }}>
          Enter entry, stop loss, and take profit to see live R:R.
        </div>
      )}
    </div>
  );
}

// ── 3. PIP VALUE CALCULATOR ──────────────────────────────────────────────
function PipValueCalc() {
  const PIPS = {
    XAUUSD: 1.0, BTCUSD: 1.0, EURUSD: 10.0, GBPUSD: 10.0, USDJPY: 9.09,
  };
  const [instr, setInstr] = useState("XAUUSD");
  const [lot, setLot] = useState("0.10");
  const [pips, setPips] = useState("20");

  const lotN = parseFloat(lot) || 0;
  const pipsN = parseFloat(pips) || 0;
  const perPip = (PIPS[instr] || 0) * lotN;
  const totalPnl = perPip * pipsN;

  return (
    <div data-testid="pip-calc">
      <Field label="Instrument" testid="pip-field-instr">
        <select data-testid="pip-instr-select" value={instr} onChange={(e) => setInstr(e.target.value)} style={inputStyle}>
          {Object.keys(PIPS).map((k) => <option key={k}>{k}</option>)}
        </select>
      </Field>
      <Field label={`Lot Size  —  ${lotN.toFixed(2)}`} testid="pip-field-lot">
        <input data-testid="pip-lot-slider" type="range" min="0.01" max="10" step="0.01"
          value={lot} onChange={(e) => setLot(e.target.value)}
          style={{ width: "100%", accentColor: T.gold }} />
        <input data-testid="pip-lot-input" type="number" inputMode="decimal" step="0.01" min="0.01" max="10"
          value={lot} onChange={(e) => setLot(e.target.value)} style={{ ...inputStyle, marginTop: 8 }} />
      </Field>
      <Field label="Number of Pips" testid="pip-field-pips">
        <input data-testid="pip-pips-input" type="number" inputMode="decimal" step="any"
          value={pips} onChange={(e) => setPips(e.target.value)} style={inputStyle} />
      </Field>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginTop: 14 }}>
        <ResultRow testid="pip-result-per-pip" label="Pip Value" value={`$${perPip.toFixed(2)} per pip`} big />
        <ResultRow testid="pip-result-total" label={`Total P&L for ${pipsN} pips`}
          value={`$${totalPnl.toFixed(2)}`} color={totalPnl >= 0 ? T.green : T.red} big />
      </div>
      <div style={{ marginTop: 10, color: T.mute, fontFamily: "monospace", fontSize: 10, letterSpacing: 1, textAlign: "center" }}>
        MT5 standard: XAU $1/pip · FX majors $10/pip · BTC $1/pip per std lot.
      </div>
    </div>
  );
}

// ── 4. PMIR SIGNAL CALCULATOR ────────────────────────────────────────────
const PMIR_PIP_FACTORS = { XAUUSD: { pipPerUnit: 10, dollarPerPip: 1, pointPerUnit: 100 }, BTCUSD: { pipPerUnit: 1, dollarPerPip: 1, pointPerUnit: 100 } };

function PmirSignalCalc({ onClose }) {
  const navigate = useNavigate();
  const [instr, setInstr] = useState("XAUUSD");
  const [entry, setEntry] = useState("");
  const [sl, setSl] = useState("");
  const [tp1, setTp1] = useState("");
  const [tp2, setTp2] = useState("");
  const [balance, setBalance] = useState("1000");
  const [risk, setRisk] = useState("1");

  const calc = useMemo(() => {
    const e = parseFloat(entry), s = parseFloat(sl), t1 = parseFloat(tp1), t2 = parseFloat(tp2);
    const bal = parseFloat(balance) || 0;
    const r = parseFloat(risk) || 0;
    if (isNaN(e) || isNaN(s) || bal <= 0 || r <= 0) return null;
    const f = PMIR_PIP_FACTORS[instr];
    const slDist = Math.abs(e - s);
    const slPips = slDist * f.pipPerUnit;
    const slPoints = slDist * f.pointPerUnit;
    if (slPips <= 0) return null;
    const dollarRisk = bal * (r / 100);
    const lot = Math.max(0, Math.floor((dollarRisk / (slPips * f.dollarPerPip)) * 100) / 100);
    const t1Pips = !isNaN(t1) ? Math.abs(t1 - e) * f.pipPerUnit : null;
    const t2Pips = !isNaN(t2) ? Math.abs(t2 - e) * f.pipPerUnit : null;
    const t1Points = !isNaN(t1) ? Math.abs(t1 - e) * f.pointPerUnit : null;
    const t2Points = !isNaN(t2) ? Math.abs(t2 - e) * f.pointPerUnit : null;
    const rr1 = t1Pips != null && slPips > 0 ? t1Pips / slPips : null;
    const rr2 = t2Pips != null && slPips > 0 ? t2Pips / slPips : null;
    const tp1Dollar = t1Pips != null ? t1Pips * f.dollarPerPip * lot : null;
    const tp2Dollar = t2Pips != null ? t2Pips * f.dollarPerPip * lot : null;
    const bestRR = Math.max(rr1 || 0, rr2 || 0);
    let validity = { color: T.green, label: "✅ Good R:R (≥1:2)" };
    if (bestRR < 1 - 1e-6) validity = { color: T.red, label: "❌ Poor R:R — review before entering" };
    else if (bestRR < 2 - 1e-6) validity = { color: T.yellow, label: "⚠️ Acceptable (1:1 to 1:2)" };
    return { lot, slPips, slPoints, t1Pips, t2Pips, t1Points, t2Points, rr1, rr2, dollarRisk, tp1Dollar, tp2Dollar, validity };
  }, [instr, entry, sl, tp1, tp2, balance, risk]);

  const logPmir = () => {
    if (!calc) return;
    const payload = {
      market: instr,
      riskPct: String(risk),
      lotSize: calc.lot,
      slPips: calc.slPips,
      dollarRisk: calc.dollarRisk,
      entry: entry,
      sl: sl,
      tp: tp1 || tp2,
      source: "PMIR Signal",
    };
    try { sessionStorage.setItem("flexx_journal_prefill", JSON.stringify(payload)); } catch (e) { console.warn("[FLEXX Tools] sessionStorage prefill failed:", e?.message || e); }
    onClose?.();
    navigate("/");
    setTimeout(() => { window.location.hash = "journal"; }, 30);
  };

  return (
    <div data-testid="pmir-calc">
      <div style={{ display: "inline-block", padding: "4px 10px", borderRadius: 999, border: `1px solid ${T.gold}`, color: T.gold, fontFamily: "monospace", fontSize: 10, letterSpacing: 1.5, marginBottom: 6 }}>
        PMIR Members · Private Signals
      </div>
      <p style={{ color: T.mute, fontSize: 12, fontStyle: "italic", marginBottom: 14 }}>
        Paste your signal. Get your lot size instantly.
      </p>

      <Field label="Instrument" testid="pmir-field-instr">
        <div style={{ display: "flex", gap: 8 }}>
          {["XAUUSD", "BTCUSD"].map((p) => (
            <button key={p} type="button" data-testid={`pmir-instr-${p}`}
              onClick={() => setInstr(p)}
              style={{
                all: "unset", cursor: "pointer", padding: "10px 18px", borderRadius: 8,
                background: instr === p ? T.gold : "transparent",
                color: instr === p ? "#0a0a0a" : T.mute,
                border: `1px solid ${instr === p ? T.gold : T.border}`,
                fontFamily: "monospace", fontSize: 12, letterSpacing: 1, fontWeight: 800,
              }}>{p}</button>
          ))}
        </div>
      </Field>
      <Field label="Entry Price" testid="pmir-field-entry"><input data-testid="pmir-entry-input" type="number" inputMode="decimal" step="any" value={entry} onChange={(e) => setEntry(e.target.value)} style={inputStyle} /></Field>
      <Field label="Stop Loss" testid="pmir-field-sl"><input data-testid="pmir-sl-input" type="number" inputMode="decimal" step="any" value={sl} onChange={(e) => setSl(e.target.value)} style={inputStyle} /></Field>
      <Field label="Take Profit 1" testid="pmir-field-tp1"><input data-testid="pmir-tp1-input" type="number" inputMode="decimal" step="any" value={tp1} onChange={(e) => setTp1(e.target.value)} style={inputStyle} /></Field>
      <Field label="Take Profit 2 (optional)" testid="pmir-field-tp2"><input data-testid="pmir-tp2-input" type="number" inputMode="decimal" step="any" value={tp2} onChange={(e) => setTp2(e.target.value)} style={inputStyle} /></Field>
      <Field label="Account Balance ($)" testid="pmir-field-balance"><input data-testid="pmir-balance-input" type="number" inputMode="decimal" step="any" value={balance} onChange={(e) => setBalance(e.target.value)} style={inputStyle} /></Field>
      <Field label={`Risk %  —  ${risk}%`} testid="pmir-field-risk">
        <input data-testid="pmir-risk-slider" type="range" min="1" max="3" step="0.1"
          value={risk} onChange={(e) => setRisk(e.target.value)} style={{ width: "100%", accentColor: T.gold }} />
      </Field>

      {calc ? (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginTop: 16 }}>
            <ResultRow testid="pmir-result-lot" label="Lot Size" value={calc.lot.toFixed(2)} big />
            <ResultRow testid="pmir-result-sl-pips" label="SL Distance" value={`${calc.slPips.toFixed(1)} pips (${Math.round(calc.slPoints)} points)`} color={T.text} />
            <ResultRow testid="pmir-result-risk" label="Dollar Risk" value={`$${calc.dollarRisk.toFixed(2)}`} color={T.text} />
            {calc.rr1 != null && <ResultRow testid="pmir-result-tp1" label={`TP1 · ${calc.t1Pips.toFixed(1)} pips (${Math.round(calc.t1Points)} pts)`} value={`1 : ${calc.rr1.toFixed(2)}  ·  $${calc.tp1Dollar.toFixed(2)}`} color={T.green} />}
            {calc.rr2 != null && <ResultRow testid="pmir-result-tp2" label={`TP2 · ${calc.t2Pips.toFixed(1)} pips (${Math.round(calc.t2Points)} pts)`} value={`1 : ${calc.rr2.toFixed(2)}  ·  $${calc.tp2Dollar.toFixed(2)}`} color={T.green} />}
          </div>
          <div data-testid="pmir-result-validity" style={{
            marginTop: 12, padding: 12, borderRadius: 10,
            background: `${calc.validity.color}11`, border: `1px solid ${calc.validity.color}`,
            color: calc.validity.color, fontWeight: 800, fontSize: 14, textAlign: "center",
          }}>{calc.validity.label}</div>
          <button data-testid="pmir-log-trade-btn" onClick={logPmir} style={goldBtn}>LOG PMIR TRADE →</button>
        </>
      ) : (
        <div style={{ marginTop: 14, padding: 14, color: T.mute, fontSize: 13, fontStyle: "italic", textAlign: "center", border: `1px dashed ${T.border}`, borderRadius: 10 }}>
          Fill entry, stop loss, balance, and risk to compute lot size.
        </div>
      )}
    </div>
  );
}

// ── 5. BTCUSD LOT CALCULATOR ─────────────────────────────────────────────
function BtcLotCalc() {
  const [balance, setBalance] = useState("1000");
  const [risk, setRisk] = useState("1");
  const [slValue, setSlValue] = useState("150");
  const [slUnit, setSlUnit] = useState("PIPS"); // "POINTS" | "PIPS"
  const [btcPrice, setBtcPrice] = useState("96500");

  // BTCUSD MT5 spec (1 std lot = 1 BTC):
  //   POINTS (MT5/Errante default): 1 point = $0.01 price move ⇒ $0.01/point per std lot
  //     Formula: Lot = Dollar Risk / (SL points × $0.01)
  //   PIPS (retail/educator convention): 1 pip = $1.00 price move = 100 points
  //     ⇒ $1.00/pip per std lot
  //     Formula: Lot = Dollar Risk / (SL pips × $1.00)
  const dollarPerUnitPerLot = slUnit === "POINTS" ? 0.01 : 1.0;
  const calc = useMemo(() => {
    const bal = Number(balance) || 0;
    const r = Number(risk) || 0;
    const sl = Number(slValue) || 0;
    const dollarRisk = bal * (r / 100);
    if (sl <= 0) return { dollarRisk, lot: 0, pipValuePer001: 0 };
    const lotRaw = dollarRisk / (sl * dollarPerUnitPerLot);
    const lot = Math.max(0, Math.floor(lotRaw * 100) / 100);
    const pipValuePer001 = 0.01 * dollarPerUnitPerLot; // value per unit at 0.01 lot
    return { dollarRisk, lot, pipValuePer001 };
  }, [balance, risk, slValue, dollarPerUnitPerLot]);

  const highExposure = calc.lot > 1.0;

  return (
    <div data-testid="btc-lot-calc">
      <div style={{ display: "inline-block", padding: "4px 10px", borderRadius: 999, border: `1px solid ${T.gold}`, color: T.gold, fontFamily: "monospace", fontSize: 10, letterSpacing: 1.5, marginBottom: 14 }}>
        BTCUSD · MT5
      </div>
      <Field label="Account Balance ($)" testid="btc-field-balance"><input data-testid="btc-balance-input" type="number" inputMode="decimal" step="any" value={balance} onChange={(e) => setBalance(e.target.value)} style={inputStyle} /></Field>
      <Field label={`Risk %  —  ${risk}%`} testid="btc-field-risk">
        <input data-testid="btc-risk-slider" type="range" min="0.5" max="5" step="0.1" value={risk} onChange={(e) => setRisk(e.target.value)} style={{ width: "100%", accentColor: T.gold }} />
      </Field>

      <Field label={`Stop Loss (${slUnit === "POINTS" ? "points" : "pips"})`} testid="btc-field-sl">
        <div data-testid="btc-sl-unit-toggle" style={{ display: "flex", gap: 6, marginBottom: 8 }}>
          {["POINTS", "PIPS"].map((u) => {
            const active = slUnit === u;
            return (
              <button key={u} type="button" data-testid={`btc-sl-unit-${u.toLowerCase()}`}
                onClick={() => setSlUnit(u)}
                style={{
                  all: "unset", cursor: "pointer", flex: 1, textAlign: "center",
                  padding: "8px 12px", borderRadius: 6,
                  background: active ? T.gold : "transparent",
                  color: active ? "#0a0a0a" : T.mute,
                  border: `1px solid ${active ? T.gold : T.border}`,
                  fontFamily: "monospace", fontSize: 11, letterSpacing: 1.5, fontWeight: 800,
                }}>{u}</button>
            );
          })}
        </div>
        <input data-testid="btc-sl-input" type="number" inputMode="decimal" step="any" value={slValue} onChange={(e) => setSlValue(e.target.value)} style={inputStyle} />
        <div data-testid="btc-sl-hint" style={{ marginTop: 6, color: T.mute, fontSize: 11, fontStyle: "italic", lineHeight: 1.5 }}>
          {slUnit === "POINTS"
            ? "Use this if MT5 shows '15000 points'"
            : "Use this if your mentor says '150 pips SL'"}
        </div>
      </Field>

      <Field label="Current BTC Price ($)" testid="btc-field-price"><input data-testid="btc-price-input" type="number" inputMode="decimal" step="any" value={btcPrice} onChange={(e) => setBtcPrice(e.target.value)} style={inputStyle} /></Field>

      <div data-testid="btc-sl-info-note" style={{
        marginBottom: 14, padding: 10, borderRadius: 8,
        background: `${T.yellow}11`, border: `1px solid ${T.yellow}55`,
        color: T.text, fontSize: 12, lineHeight: 1.5,
      }}>
        ⚠️ MT5 shows BTC SL in <b style={{ color: T.yellow }}>POINTS</b> (e.g. 15000 pts ≈ 150 pips).
        Toggle above to match how you measure your stop loss.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginTop: 16 }}>
        <ResultRow testid="btc-result-lot" label="Recommended Lot Size" value={calc.lot.toFixed(2)} big />
        <ResultRow testid="btc-result-risk" label="Dollar Risk" value={`$${calc.dollarRisk.toFixed(2)}`} color={T.text} />
        <ResultRow testid="btc-result-pipvalue"
          label={slUnit === "POINTS" ? "$ / point @ 0.01 lot" : "$ / pip @ 0.01 lot"}
          value={`$${calc.pipValuePer001.toFixed(2)}/${slUnit === "POINTS" ? "point" : "pip"}`} color={T.text} />
      </div>

      {highExposure && (
        <div data-testid="btc-warning-high-exposure" style={{
          marginTop: 12, padding: 12, borderRadius: 10,
          background: `${T.yellow}15`, border: `1px solid ${T.yellow}`, color: T.yellow,
          fontSize: 13, fontWeight: 700,
        }}>
          ⚠️ High exposure (lot &gt; 1.0) — consider splitting into multiple entries.
        </div>
      )}
    </div>
  );
}

// ── 6. PRE-TRADE CHECKLIST ───────────────────────────────────────────────
const CHECKLIST_ITEMS = [
  "I checked the current trading session (Tokyo / London / NY)",
  "I checked the DXY direction (for XAUUSD)",
  "I confirmed my lot size with the calculator",
  "My Stop Loss is set before I enter",
  "I checked the economic calendar for news",
  "My R:R is at least 1:2",
  "I am not trading out of FOMO or revenge",
  "I have a plan for both outcomes (win and loss)",
];

// AXEL one-liners — one per unchecked item (most-important first wins).
const CHECKLIST_TIPS = [
  "Asian session range, London opens with volume, NY brings reversals. Trade where YOUR edge lives — not where the market just wants to stop you.",
  "Gold inverse-correlates with DXY. Skip the trade if you don't know which way DXY is leaning.",
  "Eyeballing lot size is how accounts blow up. Two minutes with the calculator beats two months of recovery.",
  "Set the stop the second you click market. No stop, no trade — that's the rule, not a suggestion.",
  "FOMC, NFP, CPI — three letters that can flip a textbook setup into a slaughterhouse. Check the calendar.",
  "1:2 minimum is not a wish, it's a math floor. Below 1:2 you need 50%+ win rate to break even, and you don't.",
  "Revenge isn't a strategy. If you're chasing a loss, the only winning move is to close the platform and walk.",
  "A trade plan that says 'I'll figure it out if it goes against me' is not a plan. It's a panic schedule.",
];

// Fallback rotation when all 8 are checked — daily one-liners.
const ALL_CHECKED_TIPS = [
  "Discipline compounds. Show up like this every trade and your future self pays you back.",
  "The best traders aren't right more often — they prepare more often. You're doing the work.",
  "A clean checklist won't guarantee a winning trade. But a missed checklist guarantees a sloppy one.",
  "Process before profit. You just earned the right to take this trade.",
  "Eight checks. Most traders do zero. You're already in the top 5%.",
  "Conviction is built one disciplined entry at a time. This is rep #N — keep counting.",
  "Pre-trade prep is the cheapest insurance policy in finance. Premium paid.",
];

function PreTradeChecklist() {
  const [checks, setChecks] = useState(() => CHECKLIST_ITEMS.map(() => false));
  const passed = checks.filter(Boolean).length;
  const all = passed === CHECKLIST_ITEMS.length;
  const pct = (passed / CHECKLIST_ITEMS.length) * 100;
  const toggle = (i) => setChecks(checks.map((v, idx) => (idx === i ? !v : v)));
  const reset = () => setChecks(CHECKLIST_ITEMS.map(() => false));

  // First unchecked → AXEL targets that. If all checked → daily-rotating tip.
  const tip = useMemo(() => {
    if (!all) {
      const firstUnchecked = checks.findIndex((v) => !v);
      return CHECKLIST_TIPS[firstUnchecked] || CHECKLIST_TIPS[0];
    }
    const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0)) / 86_400_000);
    return ALL_CHECKED_TIPS[dayOfYear % ALL_CHECKED_TIPS.length];
  }, [checks, all]);

  return (
    <div data-testid="pre-trade-checklist">
      <p style={{ color: T.mute, fontSize: 13, fontStyle: "italic", marginBottom: 14 }}>
        Complete before every entry.
      </p>

      <div style={{ marginBottom: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, fontFamily: "monospace", color: T.mute, marginBottom: 6 }}>
          <span>PROGRESS</span>
          <span data-testid="checklist-progress-text">{passed} / {CHECKLIST_ITEMS.length}</span>
        </div>
        <div style={{ height: 8, background: T.border, borderRadius: 4, overflow: "hidden" }}>
          <div data-testid="checklist-progress-bar" style={{
            width: `${pct}%`, height: "100%",
            background: `linear-gradient(90deg, ${T.gold}, #E8B94F)`,
            transition: "width 0.3s",
          }} />
        </div>
      </div>

      <div style={{ display: "grid", gap: 8 }}>
        {CHECKLIST_ITEMS.map((label, i) => (
          <button key={i} type="button" data-testid={`checklist-item-${i}`}
            onClick={() => toggle(i)}
            style={{
              all: "unset", cursor: "pointer",
              display: "flex", alignItems: "center", gap: 12,
              padding: "12px 14px", borderRadius: 8,
              background: checks[i] ? `${T.gold}10` : "#0a0e13",
              border: `1px solid ${checks[i] ? T.gold : T.border}`,
              borderLeft: `3px solid ${checks[i] ? T.gold : T.border}`,
              transition: "all 0.18s",
            }}>
            <span style={{
              width: 20, height: 20, borderRadius: 4,
              border: `2px solid ${checks[i] ? T.gold : T.border}`,
              background: checks[i] ? T.gold : "transparent",
              color: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 14, fontWeight: 900, flexShrink: 0,
            }}>{checks[i] ? "✓" : ""}</span>
            <span style={{ color: checks[i] ? T.text : T.mute, fontSize: 13, lineHeight: 1.5 }}>
              {label}
            </span>
          </button>
        ))}
      </div>

      {/* AXEL Pro Tip — micro-coaching tied to first unchecked item or daily rotation. */}
      <div data-testid="checklist-axel-tip" style={{
        marginTop: 14, padding: 12, borderRadius: 10,
        background: `${T.gold}0e`, border: `1px solid ${T.gold}55`, borderLeft: `3px solid ${T.gold}`,
        display: "flex", gap: 10, alignItems: "flex-start",
      }}>
        <span style={{ fontSize: 16, lineHeight: 1.2, flexShrink: 0 }}>💡</span>
        <div>
          <div style={{ fontFamily: "monospace", color: T.gold, fontSize: 9, letterSpacing: 2, marginBottom: 4 }}>
            PRO TIP FROM AXEL
          </div>
          <div style={{ color: T.text, fontSize: 12.5, lineHeight: 1.55, fontStyle: "italic" }}>
            {tip}
          </div>
        </div>
      </div>

      {all && (
        <div data-testid="checklist-ready-banner" style={{
          marginTop: 14, padding: 16, borderRadius: 10,
          background: `linear-gradient(135deg, ${T.gold}, #E8B94F)`,
          color: "#0a0a0a", textAlign: "center", fontWeight: 800, fontSize: 15,
        }}>
          ✅ You're ready to trade. Execute with confidence.
        </div>
      )}

      <button data-testid="checklist-reset-btn" onClick={reset} style={{
        ...goldBtn,
        background: "transparent", color: T.mute,
        border: `1px solid ${T.border}`, fontWeight: 700,
      }}>
        CLEAR & START NEW TRADE
      </button>
    </div>
  );
}
